import { useEffect, useMemo, useRef } from "react";
import client from "libs/client";
import type { ProgramGameCompetitionEditionOption, ProgramGameSelectOption } from "../types";

function resolveTeamId(team: ProgramGameSelectOption | null): number | null {
	if (team == null) {
		return null;
	}
	const teamId = Number(team.value);
	if (Number.isNaN(teamId) || teamId <= 0) {
		return null;
	}
	return teamId;
}

function mapEditionOptions(
	rows: Array<{
		value?: number;
		label?: string;
		sharedByBothTeams?: boolean;
		competitionCountry?: string;
	}>,
): ProgramGameCompetitionEditionOption[] {
	return rows.map((option) => ({
		value: option.value,
		label: option.label ?? "",
		sharedByBothTeams: option.sharedByBothTeams === true,
		competitionCountry: option.competitionCountry,
	}));
}

type TeamIdsSnapshot = {
	homeTeamId: number | null;
	awayTeamId: number | null;
};

/**
 * Competition edition options for the program game modal.
 * Catalog is fetched once (no team ids). Shared-edition lookup runs only when
 * both teams are selected and is used for auto-select — it does not refetch
 * or disable the full catalog select.
 */
export function useProgramGameCompetitionEditionOptions(
	isOpen: boolean,
	homeTeam: ProgramGameSelectOption | null,
	awayTeam: ProgramGameSelectOption | null,
	selectedEdition: ProgramGameCompetitionEditionOption | null,
	onEditionChange: (edition: ProgramGameCompetitionEditionOption | null) => void,
): {
	competitionEditionOptions: ProgramGameCompetitionEditionOption[];
	competitionEditionsLoading: boolean;
} {
	const homeTeamId = resolveTeamId(homeTeam);
	const awayTeamId = resolveTeamId(awayTeam);
	const previousTeamIdsRef = useRef<TeamIdsSnapshot | null>(null);
	const pendingAutoSelectRef = useRef(false);
	const bothTeamsSelected = homeTeamId !== null && awayTeamId !== null;

	const {
		data: catalogResponse,
		isLoading: catalogLoading,
	} = client.getDiscoverGamesProgramGameCompetitionEditions.useQuery(
		{},
		{
			enabled: isOpen,
			staleTime: 5 * 60 * 1000,
		},
	);

	const {
		data: sharedResponse,
		isError: sharedIsError,
	} = client.getDiscoverGamesProgramGameCompetitionEditions.useQuery(
		{
			homeTeamId: homeTeamId ?? undefined,
			awayTeamId: awayTeamId ?? undefined,
		},
		{
			enabled: isOpen && bothTeamsSelected,
		},
	);

	const competitionEditionOptions = useMemo(
		() => mapEditionOptions(catalogResponse?.data ?? []),
		[catalogResponse],
	);

	const sharedEditionOptions = useMemo(
		() => mapEditionOptions(sharedResponse?.data ?? []),
		[sharedResponse],
	);

	// Only the initial catalog fetch disables the select — shared lookup is background.
	const competitionEditionsLoading = isOpen && catalogLoading;
	// With cacheTime: 0, the first enabled render often has no data and both
	// isLoading/isFetching false — wait for a response (or error) before settling.
	const sharedLookupPending =
		bothTeamsSelected && sharedResponse == null && sharedIsError === false;

	useEffect(() => {
		if (isOpen === false) {
			previousTeamIdsRef.current = null;
			pendingAutoSelectRef.current = false;
			return;
		}

		const previousTeamIds = previousTeamIdsRef.current;
		const teamsChanged =
			previousTeamIds === null ||
			previousTeamIds.homeTeamId !== homeTeamId ||
			previousTeamIds.awayTeamId !== awayTeamId;

		if (teamsChanged) {
			previousTeamIdsRef.current = { homeTeamId, awayTeamId };
			pendingAutoSelectRef.current = true;
		}

		if (pendingAutoSelectRef.current === false) {
			return;
		}

		if (competitionEditionsLoading) {
			return;
		}

		const selectedStillAvailable =
			selectedEdition != null &&
			competitionEditionOptions.some((option) => option.value === selectedEdition.value);

		if (selectedStillAvailable) {
			pendingAutoSelectRef.current = false;
			return;
		}

		if (bothTeamsSelected === false) {
			pendingAutoSelectRef.current = false;
			return;
		}

		if (sharedLookupPending) {
			return;
		}

		pendingAutoSelectRef.current = false;

		if (sharedEditionOptions.length === 1) {
			onEditionChange(sharedEditionOptions[0]);
		}
	}, [
		bothTeamsSelected,
		competitionEditionOptions,
		competitionEditionsLoading,
		homeTeamId,
		awayTeamId,
		isOpen,
		onEditionChange,
		selectedEdition,
		sharedEditionOptions,
		sharedLookupPending,
	]);

	return {
		competitionEditionOptions,
		competitionEditionsLoading,
	};
}
